/**
 * Email Templates Tab Component
 * Coláiste Lurgan Management System - Email template editing for ADMIN and OFFICE roles
 *
 * Two-panel layout: template list on the left, editor on the right.
 * Variable chips allow click-to-insert at cursor position.
 * Validation checks that required variables (e.g. {{ reset_link }}) are present.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import RichTextEditor from '../ui/RichTextEditor';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Save, Mail, CheckCircle2, AlertTriangle, Plus, Eye } from 'lucide-react';
import api from '../../lib/api';

// ─── Validation helper ───────────────────────────────────────────────────────

function getMissingRequired(template) {
  if (!template?.variables) return [];
  return template.variables
    .filter(v => v.required)
    .filter(v => !template.body_text?.includes(`{{ ${v.name} }}`));
}

// ─── Template list item ───────────────────────────────────────────────────────

function TemplateListItem({ template, selected, onClick }) {
  const missing = getMissingRequired(template);
  const hasIssue = missing.length > 0;

  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left px-4 py-3 rounded-lg border transition-colors',
        selected
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-background hover:bg-muted border-border',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Mail className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium text-sm truncate">{template.label}</span>
        </div>
        {hasIssue ? (
          <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${selected ? 'text-yellow-300' : 'text-yellow-500'}`} />
        ) : (
          <CheckCircle2 className={`h-4 w-4 flex-shrink-0 ${selected ? 'text-green-300' : 'text-green-500'}`} />
        )}
      </div>
      {template.description && (
        <p className={`text-xs mt-1 truncate ${selected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
          {template.description}
        </p>
      )}
    </button>
  );
}

// ─── Variable chip ────────────────────────────────────────────────────────────

function VariableChip({ variable, onInsert }) {
  const [flash, setFlash] = useState(false);

  const handleClick = () => {
    onInsert(variable.name);
    setFlash(true);
    setTimeout(() => setFlash(false), 600);
  };

  return (
    <button
      onClick={handleClick}
      title={`Insert {{ ${variable.name} }}`}
      className={[
        'inline-flex items-center gap-1 px-2 py-1 rounded text-xs border transition-all',
        variable.required
          ? 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100'
          : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        flash ? 'scale-95 opacity-70' : '',
      ].join(' ')}
    >
      <Plus className="h-3 w-3" />
      {variable.label}
      {variable.required && <span className="text-orange-500">*</span>}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function EmailTemplatesTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const editorRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await api.emailTemplates.getAll();
      setTemplates(data);
      if (data.length > 0) setSelectedName(data[0].name);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      toast({
        variant: 'destructive',
        title: t('settings.emailTemplates.fetchError', 'Failed to load email templates'),
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const selected = templates.find(t => t.name === selectedName) || null;
  const missing = getMissingRequired(selected);

  const handleChange = useCallback((field, value) => {
    setTemplates(prev =>
      prev.map(tpl => tpl.name === selectedName ? { ...tpl, [field]: value } : tpl)
    );
  }, [selectedName]);

  const handleInsertVariable = useCallback((varName) => {
    const token = `{{ ${varName} }}`;
    editorRef.current?.insertAtCursor(token);
  }, []);

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const updated = await api.emailTemplates.update(selected.name, {
        subject: selected.subject,
        body_text: selected.body_text,
      });
      // Merge updated response back into templates state
      setTemplates(prev => prev.map(t => t.name === updated.name ? { ...t, ...updated } : t));
      toast({ title: t('settings.emailTemplates.saveSuccess', 'Template saved') });
    } catch (err) {
      console.error('Error saving template:', err);
      toast({
        variant: 'destructive',
        title: t('settings.emailTemplates.saveError', 'Failed to save template'),
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('settings.emailTemplates.noTemplates', 'No email templates found.')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4">

      {/* ── Left: template list ─────────────────────────────────── */}
      <div className="lg:w-56 xl:w-64 flex-shrink-0 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
          {t('settings.emailTemplates.templates', 'Templates')}
        </p>
        {templates.map(tpl => (
          <TemplateListItem
            key={tpl.name}
            template={tpl}
            selected={tpl.name === selectedName}
            onClick={() => setSelectedName(tpl.name)}
          />
        ))}
        <p className="text-xs text-muted-foreground px-1 pt-1">
          {t('settings.emailTemplates.requiredNote', '* Required in message body')}
        </p>
      </div>

      {/* ── Right: editor ──────────────────────────────────────── */}
      {selected && (
        <Card className="flex-1 min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{selected.label}</CardTitle>
            {selected.description && (
              <CardDescription>{selected.description}</CardDescription>
            )}
          </CardHeader>

          <CardContent className="space-y-5">

            {/* Validation warning — shown at top so it's immediately visible */}
            {missing.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-yellow-500" />
                <div>
                  <span className="font-medium">
                    {t('settings.emailTemplates.missingVars', 'Missing required variables:')}
                  </span>
                  {' '}
                  {missing.map(v => (
                    <code key={v.name} className="mx-0.5 px-1 rounded bg-yellow-100 text-yellow-900 text-xs">
                      {'{{ '}{v.name}{' }}'}
                    </code>
                  ))}
                </div>
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="tpl-subject">
                {t('settings.emailTemplates.subject', 'Subject')}
              </Label>
              <Input
                id="tpl-subject"
                value={selected.subject || ''}
                onChange={e => handleChange('subject', e.target.value)}
              />
            </div>

            {/* Variable chips */}
            {selected.variables?.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  {t('settings.emailTemplates.insertVariable', 'Insert variable')}
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {selected.variables.map(v => (
                    <VariableChip
                      key={v.name}
                      variable={v}
                      onInsert={handleInsertVariable}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Email body */}
            <div className="space-y-1.5">
              <Label>
                {t('settings.emailTemplates.body', 'Message')}
              </Label>

              {selected.greeting && (
                <div className="rounded-md border bg-muted/60 px-3 py-2 text-muted-foreground select-none text-xs">
                  {selected.greeting.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}

              <RichTextEditor
                ref={editorRef}
                value={selected.body_text || ''}
                onChange={(content) => handleChange('body_text', content)}
                placeholder={t('settings.emailTemplates.bodyPlaceholder', 'Type the email message here...')}
              />

              {selected.signoff && (
                <div className="rounded-md border bg-muted/60 px-3 py-2 text-muted-foreground select-none text-xs">
                  {selected.signoff.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {t('settings.emailTemplates.fixedNote', 'The greeting and sign-off are fixed and cannot be changed here.')}
              </p>
            </div>

            {/* Save + Preview */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPreview(true)} disabled={!selected?.html_content}>
                <Eye className="h-4 w-4 mr-2" />
                {t('settings.emailTemplates.preview', 'Preview')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Save className="h-4 w-4 mr-2" />
                }
                {saving
                  ? t('settings.emailTemplates.saving', 'Saving...')
                  : t('settings.emailTemplates.save', 'Save Template')
                }
              </Button>
            </div>

          </CardContent>
        </Card>
      )}
      {/* ── Preview modal ──────────────────────────────────────── */}
      {selected && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl w-full p-0 overflow-hidden">
            <DialogHeader className="px-6 pt-5 pb-3 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                {selected.label} — {t('settings.emailTemplates.preview', 'Preview')}
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {t('settings.emailTemplates.previewNote', 'Shows the last saved version. Template variables (e.g. {{ name }}) will be filled in when sent.')}
              </p>
            </DialogHeader>
            <div className="overflow-y-auto" style={{ maxHeight: '72vh' }}>
              <iframe
                srcDoc={selected.html_content?.replace(/\{\{ logo_url \}\}/g, `${window.location.origin}/lurgan-logo-white.png`)}
                title="Email preview"
                className="w-full border-0"
                style={{ height: '640px' }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default EmailTemplatesTab;
